import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { FormInput } from '../../../../../components/FormInput/FormInput';
import { Modal } from '../../../../../components/Modal/Modal';

import './MilestoneTracker.scss';

/**
 * AddMilestone Component
 *
 * A reusable modal component for adding and editing milestones.
 * Follows clean code principles with proper separation of concerns.
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function called when modal is closed
 * @param {function} onConfirm - Function called when form is submitted
 * @param {object} control - React Hook Form control object
 * @param {boolean} isEditing - Whether in edit mode or add mode
 * @param {string} editingTitle - Title of milestone being edited (if in edit mode)
 * @param {function} handleSubmit - React Hook Form submit handler
 */

const AddMilestone = ({
  isOpen,
  onClose,
  onConfirm,
  control,
  isEditing = false,
  editingTitle = '',
  handleSubmit,
}) => {
  // Memoized form render function for performance
  const renderMilestoneForm = useCallback(
    () => (
      <form onSubmit={handleSubmit} className='milestone-form'>
        <Controller
          name='title'
          control={control}
          rules={{ required: 'Milestone title is required' }}
          render={({ field, fieldState: { error } }) => (
            <FormInput
              id='milestone-title'
              label='Milestone Title'
              type='text'
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder='Enter milestone title'
              required
              error={error?.message}
              showErrorMsg={false}
            />
          )}
        />

        <Controller
          name='description'
          control={control}
          rules={{ required: 'Description is required' }}
          render={({ field, fieldState: { error } }) => (
            <FormInput
              id='milestone-description'
              label='Description'
              type='textarea'
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder='Enter milestone description'
              rows={4}
              required
              error={error?.message}
              showErrorMsg={false}
            />
          )}
        />

        <Controller
          name='amount_usd'
          control={control}
          rules={{
            required: 'Amount is required',
            pattern: {
              value: /^\d*\.?\d*$/,
              message: 'Please enter a valid amount',
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <FormInput
              id='milestone-amount'
              label='Amount (USD)'
              type='number'
              value={field.value}
              onChange={(value) => field.onChange(Number(value))}
              onBlur={field.onBlur}
              placeholder='Enter amount in USD'
              required
              error={error?.message}
              showErrorMsg={false}
            />
          )}
        />

        <Controller
          name='due_date'
          control={control}
          rules={{ required: 'Due date is required' }}
          render={({ field, fieldState: { error } }) => (
            <FormInput
              id='milestone-due-date'
              label='Due Date'
              type='date'
              value={field.value}
              onChange={(value) => field.onChange(value)}
              onBlur={field.onBlur}
              required
              error={error?.message}
              showErrorMsg={false}
            />
          )}
        />
      </form>
    ),
    [control, handleSubmit]
  );

  // Don't render if modal is not open
  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={isEditing ? 'Edit Milestone' : 'Add New Milestone'}
      confirmText={isEditing ? 'Update' : 'Add'}
      modalContentClassName='milestone-modal'
      closeOnOverlayClick={false}
      confirmVariant='success'
    >
      {renderMilestoneForm()}
    </Modal>
  );
};

AddMilestone.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  isEditing: PropTypes.bool,
  editingTitle: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
};

export default AddMilestone;
